import { z } from 'zod'
import type { InkoopFactuurStatus } from './inkoop'
import type { VerkoopFactuurConceptStatus } from './verkoop-mail'

// ─── batch-65 factuur-richting (FO-486) ──────────────────────────────────────
// Een binnengekomen factuur in de verkeerde bak: een inkoopfactuur (bv.
// Finqle-kosten aan STREEP) op verkoopfactuur@, of een eigen verkoopfactuur op
// inkoop@. Met één knop verplaatsen - zonder herupload, met behoud van de
// mail-herkomst - plus voorsorteren op de richting en dezelfde knop andersom.
// Gedeeld door API (routes/verkoop-mail.ts, routes/inkoop.ts) en backoffice.

export const FACTUUR_RICHTINGEN = ['VERKOOP', 'INKOOP', 'INTERN'] as const
export type FactuurRichtingWaarde = (typeof FACTUUR_RICHTINGEN)[number]

export const FACTUUR_RICHTING_ZEKERHEDEN = ['HOOG', 'MIDDEL', 'LAAG'] as const
export type FactuurRichtingZekerheid = (typeof FACTUUR_RICHTING_ZEKERHEDEN)[number]

export const FACTUUR_RICHTING_LABELS: Record<FactuurRichtingWaarde, string> = {
  VERKOOP: 'Verkoopfactuur (STREEP is de afzender)',
  INKOOP: 'Inkoopfactuur (STREEP is de ontvanger)',
  INTERN: 'Interne factuur (tussen eigen BV\'s)',
}

/** Badge-/markeringsteksten; één plek zodat postvak, wachtrij en review gelijk zijn. */
export const WAARSCHIJNLIJK_INKOOP_LABEL = 'Waarschijnlijk inkoopfactuur'
export const WAARSCHIJNLIJK_VERKOOP_LABEL = 'Waarschijnlijk verkoopfactuur'
export const FINQLE_KOSTENFACTUUR_LABEL = 'Finqle-kostenfactuur'
export const VERMOEDELIJK_INKOOP_FILTER_LABEL = 'Vermoedelijk inkoop'
export const VERKEERDE_BAK_LABEL = 'Vermoedelijk verkeerde bak'

/**
 * Samenvatting van het opgeslagen richting-oordeel (beoordeelFactuurRichting)
 * op een verkoopconcept of inkoopfactuur. Alleen eigen BV-namen, geen
 * factuurinhoud.
 */
export interface FactuurRichtingSamenvattingDTO {
  richting: FactuurRichtingWaarde | null
  zekerheid: FactuurRichtingZekerheid
  reden: string
  afzenderBv: { id: string; naam: string } | null
  ontvangerBv: { id: string; naam: string } | null
}

// ─── Verkoop -> inkoop ───────────────────────────────────────────────────────

/**
 * Of "Boeken als inkoopfactuur" kan, en zo niet waarom (spec §6):
 *   BESCHIKBAAR    - kan (knop actief);
 *   INTERN         - afzender én ontvanger een eigen BV: het intern-pad (FO-432)
 *                    wint, de knop toont "intern" in plaats van inkoop;
 *   GEEN_RECHT     - de gebruiker mist FACTUREN.canEdit of INKOOP.canEdit
 *                    (knop zichtbaar-uitgeschakeld met uitleg; de API geeft 403);
 *   AL_GEBOEKT     - er bestaat al een (niet-verwijderde) Invoice: eerst die
 *                    factuur verwijderen (FO-443), daarna kan het alsnog;
 *   AL_VERPLAATST  - al als inkoopfactuur geboekt (doorklik);
 *   NIET_OPEN      - verwijderd, of in een status die niet verplaatst kan worden.
 * Een AFGEWEZEN concept is gewoon BESCHIKBAAR (afwijzen was vóór FO-486 de enige
 * uitweg voor een inkoopfactuur in het verkoop-postvak).
 */
export const BOEK_ALS_INKOOP_MODI = [
  'BESCHIKBAAR',
  'INTERN',
  'GEEN_RECHT',
  'AL_GEBOEKT',
  'AL_VERPLAATST',
  'NIET_OPEN',
] as const
export type BoekAlsInkoopModus = (typeof BOEK_ALS_INKOOP_MODI)[number]

export interface BoekAlsInkoopBeschikbaarheidDTO {
  modus: BoekAlsInkoopModus
  /** Uitleg voor een niet-beschikbare knop (tooltip + kaart); null bij BESCHIKBAAR. */
  uitleg: string | null
}

export const KOSTENSOORT_VOORSTEL_BRONNEN = ['LEVERANCIER_DEFAULT', 'FINQLE', 'GEEN'] as const
export type KostensoortVoorstelBronDTO = (typeof KOSTENSOORT_VOORSTEL_BRONNEN)[number]

/** Voorstel voor de bevestigingsdialoog (GET /verkoop-mail/concepten/:id/boek-als-inkoop). */
export interface BoekAlsInkoopVoorstelDTO {
  conceptId: string
  beschikbaarheid: BoekAlsInkoopBeschikbaarheidDTO
  factuurnummer: string | null
  factuurdatum: string | null
  /** Bedragen als string (DTO-conventie), de UI formatteert. */
  bedrag: { subtotaal: string | null; btwBedrag: string | null; totaal: string | null }
  /** De uitgever volgens de extractie (wordt de leverancier). */
  afzender: { naam: string | null; kvk: string | null }
  /** De geadresseerde volgens de extractie + de herkende eigen BV. */
  ontvanger: { naam: string | null; bv: { id: string; naam: string } | null }
  /** Waarom dit een inkoopfactuur is: herkenning, AI-richting of handmatige keuze. */
  reden: { bron: 'HERKENNING' | 'AI_RICHTING' | 'HANDMATIG'; tekst: string }
  richting: FactuurRichtingSamenvattingDTO | null
  finqleKostenfactuur: boolean
  /** Voorgevulde ontvangende BV (EntityPicker). */
  voorgesteldeEntityId: string | null
  kostensoortVoorstel: {
    /**
     * Soort uit de kostensoorten-stamtabel (FO-482; batch-65 integratie: was de
     * oude enum). null = geen voorstel.
     */
    kostensoortId: string | null
    /** "Hoofdgroep › Soort" van het voorstel (null zonder voorstel). */
    label: string | null
    bron: KostensoortVoorstelBronDTO
    toelichting: string
  }
  /** Bestaande leverancier bij deze afzender, of null (dan komt er een concept-leverancier). */
  leverancier: { id: string; naam: string } | null
  /**
   * Staat dezelfde PDF (of hetzelfde nummer bij deze leverancier) al bij inkoop?
   *   GEEN       - nee, verplaatsen maakt een nieuwe concept-inkoopfactuur;
   *   BESTAAT    - ja: verplaatsen koppelt eraan (geen tweede factuur);
   *   VERWIJDERD - dezelfde factuur is bij inkoop eerder verwijderd: verplaatsen
   *                kan dan niet (hetzelfde nummer mag er geen tweede keer in).
   */
  alBijInkoop: 'GEEN' | 'BESTAAT' | 'VERWIJDERD'
  /**
   * De bestaande inkoopfactuur (bij BESTAAT/VERWIJDERD). Alleen gevuld voor wie
   * Inkoop mag inzien: nummer en status zijn inkoopgegevens (review K7).
   */
  bestaandeInkoopFactuur: { id: string; factuurNr: string; status: InkoopFactuurStatus } | null
  /** FO-402 - AI gepauzeerd: verplaatsen werkt (de extractie wordt hergebruikt). */
  aiPauze: boolean
}

/** Body van POST /verkoop-mail/concepten/:id/boek-als-inkoop (gedeeld form + API). */
export const boekAlsInkoopSchema = z.object({
  /** Ontvangende STREEP-BV; weglaten = de herkende/gematchte BV. */
  entityId: z.string().min(1, 'Kies een BV').nullable().optional(),
  /**
   * Kostensoort (stamtabel-id, FO-482) voor de regels zonder eigen soort;
   * weglaten = het voorstel, null = geen kostensoort toepassen.
   */
  kostensoortId: z.string().min(1, 'Kies een kostensoort').nullable().optional(),
  /** Optionele toelichting (komt in het auditspoor van beide kanten). */
  reden: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
})
export type BoekAlsInkoopInput = z.infer<typeof boekAlsInkoopSchema>

/**
 * Uitkomst van het verplaatsen (spec §3.4):
 *   AANGEMAAKT          - nieuwe concept-inkoopfactuur uit dezelfde blob;
 *   GEKOPPELD_DUPLICAAT - dezelfde PDF / hetzelfde nummer stond al bij inkoop:
 *                         gekoppeld, geen tweede factuur;
 *   HEROPEND            - de inkoopfactuur was eerder naar verkoop verplaatst en
 *                         staat nu weer ter controle bij inkoop.
 * "Geen factuur" en "mislukt" zijn fouten (422) - het concept blijft dan ongewijzigd.
 */
export const BOEK_ALS_INKOOP_UITKOMSTEN = ['AANGEMAAKT', 'GEKOPPELD_DUPLICAAT', 'HEROPEND'] as const
export type BoekAlsInkoopUitkomst = (typeof BOEK_ALS_INKOOP_UITKOMSTEN)[number]

export interface BoekAlsInkoopResultaatDTO {
  uitkomst: BoekAlsInkoopUitkomst
  inkoopFactuur: { id: string; factuurNr: string; status: InkoopFactuurStatus }
  /** Aandachtspunten van de inkoop-kant (nieuwe leverancier, BV onbekend, ...). */
  markeringen: string[]
  /** Eén zin voor de dialoog. */
  melding: string
}

/** Foutcodes van de verplaats-routes (inline in de dialoog, nooit een kale toast). */
export const FACTUUR_RICHTING_FOUTCODES = [
  'GEEN_FACTUUR',
  'VERPLAATSEN_MISLUKT',
  'BRON_PDF_ONTBREEKT',
  'INTERN_PAD',
  'AL_GEBOEKT',
  'REEDS_BEOORDEELD',
  'DUPLICAAT_VERKOOPFACTUUR',
  /** Dezelfde factuur is bij inkoop eerder verwijderd (review I2). */
  'DUPLICAAT_VERWIJDERD',
  /** De inkoopfactuur staat op een nog open betaallijst. */
  'OP_BETAALLIJST',
  /** batch-65 integratie: de inkoopfactuur is (deels) doorbelast aan een klant. */
  'DOORBELAST',
] as const
export type FactuurRichtingFoutcode = (typeof FACTUUR_RICHTING_FOUTCODES)[number]

// ─── Inkoop -> verkoop ───────────────────────────────────────────────────────

/**
 * Of "Boeken als verkoopfactuur" kan op een inkoopfactuur:
 *   BESCHIKBAAR          - concept ter controle (teControleren, niet geboekt);
 *   GEEN_RECHT           - FACTUREN.canEdit én INKOOP.canEdit vereist;
 *   NIET_TE_CONTROLEREN  - al bevestigd/geboekt, betaald of geannuleerd;
 *   AL_VERPLAATST        - al als verkoopconcept doorgezet (doorklik);
 *   INTERN               - intercompany (FO-432): blijft op het interne pad;
 *   OP_BETAALLIJST       - staat op een nog open betaallijst: eerst daar af
 *                          (anders zou de SEPA-run hem alsnog betalen);
 *   DOORBELAST           - (batch-65 integratie) er loopt een actieve
 *                          doorbelasting aan een klant (FO-483): eerst ontkoppelen.
 */
export const BOEK_ALS_VERKOOP_MODI = [
  'BESCHIKBAAR',
  'GEEN_RECHT',
  'NIET_TE_CONTROLEREN',
  'AL_VERPLAATST',
  'INTERN',
  'OP_BETAALLIJST',
  'DOORBELAST',
] as const
export type BoekAlsVerkoopModus = (typeof BOEK_ALS_VERKOOP_MODI)[number]

export interface BoekAlsVerkoopBeschikbaarheidDTO {
  modus: BoekAlsVerkoopModus
  uitleg: string | null
}

/** Body van POST /inkoop/facturen/:id/boek-als-verkoop. */
export const boekAlsVerkoopSchema = z.object({
  reden: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
})
export type BoekAlsVerkoopInput = z.infer<typeof boekAlsVerkoopSchema>

/**
 * AANGEMAAKT - nieuw verkoopconcept (TE_CONTROLEREN) uit dezelfde blob;
 * GEKOPPELD_DUPLICAAT - dezelfde PDF stond al als verkoopconcept (open of
 *   goedgekeurd): gekoppeld, geen tweede;
 * HEROPEND - dat concept was eerder naar inkoop verplaatst of afgewezen en
 *   staat nu weer ter controle.
 */
export const BOEK_ALS_VERKOOP_UITKOMSTEN = ['AANGEMAAKT', 'GEKOPPELD_DUPLICAAT', 'HEROPEND'] as const
export type BoekAlsVerkoopUitkomst = (typeof BOEK_ALS_VERKOOP_UITKOMSTEN)[number]

export interface BoekAlsVerkoopResultaatDTO {
  uitkomst: BoekAlsVerkoopUitkomst
  verkoopConcept: { id: string; status: VerkoopFactuurConceptStatus }
  melding: string
}

/** Herkomst "via verkoopfactuur@, verplaatst naar inkoop door ... op ...". */
export interface HerkomstVerkoopPostvakDTO {
  conceptId: string
  bronBestand: string
  mailAfzender: string | null
  mailOntvangenOp: string | null
  /** Naam van wie verplaatste; null = automatisch (instelling). */
  verplaatstDoor: string | null
  verplaatstOp: string | null
  automatisch: boolean
}

/** Richting-/herkomstblok op de inkoop-review (GET /inkoop/facturen/:id/richting). */
export interface InkoopFactuurRichtingDTO {
  richting: FactuurRichtingSamenvattingDTO | null
  vermoedelijkVerkoop: boolean
  /** Vanuit het verkoop-postvak hierheen verplaatst. */
  herkomstVerkoop: HerkomstVerkoopPostvakDTO | null
  /** Naar verkoop verplaatst (status VERPLAATST_NAAR_VERKOOP). */
  naarVerkoop: {
    conceptId: string
    conceptStatus: VerkoopFactuurConceptStatus
    verplaatstDoor: string | null
    verplaatstOp: string | null
  } | null
  boekAlsVerkoop: BoekAlsVerkoopBeschikbaarheidDTO
  /** Mag deze gebruiker Facturen inzien? Anders geen doorklik naar het verkoopconcept (K7). */
  facturenInzage: boolean
}

/** Werkvoorraad-tellers op de inkoop-wachtrij (server-side geteld). */
export interface InkoopRichtingTellersDTO {
  /** Te controleren inkoopfacturen die met hoge zekerheid een verkoopfactuur zijn. */
  vermoedelijkVerkoop: number
  /** Te controleren inkoopfacturen die uit het verkoop-postvak komen. */
  uitVerkoopPostvak: number
}

// ─── Instellingen (per postvak) ──────────────────────────────────────────────

export const FINQLE_AFZENDERKENMERK_MAX = 20

/**
 * Instelling van de richting-herkenning (SystemSetting). Finqle-kenmerken zijn
 * domeinen, adressen of namen van de uitgever (niets hardcoded; de operator
 * vult ze). Automatisch boeken als inkoop staat per definitie UIT (spec A2) en
 * geldt alleen voor het verkoop-postvak, bij een HOGE zekerheid.
 */
export const factuurRichtingInstellingenSchema = z.object({
  finqleAfzenderKenmerken: z
    .array(
      z
        .string()
        .trim()
        .min(3, 'Een kenmerk heeft minstens 3 tekens')
        .max(120, 'Een kenmerk is maximaal 120 tekens'),
    )
    .max(FINQLE_AFZENDERKENMERK_MAX, `Maximaal ${FINQLE_AFZENDERKENMERK_MAX} kenmerken`),
  autoBoekenAlsInkoop: z.boolean(),
})
export type FactuurRichtingInstellingen = z.infer<typeof factuurRichtingInstellingenSchema>

export const FACTUUR_RICHTING_INSTELLINGEN_DEFAULT: FactuurRichtingInstellingen = {
  finqleAfzenderKenmerken: [],
  autoBoekenAlsInkoop: false,
}
